<template>
  <Modal
    title="Change Department"
    class-name="department-edit-modal"
    :submit-btn-text="submitButtonText"
    @submit="handleSave"
    @hide-modal="$emit('hide-modal')"
  >
    <div>
      <Select
        v-model="updatedDepartmentId"
        name="Department"
        :options="departmentOptions"
        placeholder="Select Department"
        label="Owner"
        class="mb-2"
      >
        <template #item-row="{ option }">
          <div class="option-row">
            <TruncateText
              :class="{ 'strike-through': option.archived }"
              :text="option.label"
            />
          </div>
        </template>
      </Select>
    </div>

    <ConfirmChangeModal
      v-if="confirmModalVisible"
      :department-change-preview="departmentChangePreview"
      @hide-modal="hideConfirmModal"
      @confirmed="handleChangesConfirmed"
    />
  </Modal>
</template>

<script>
import sortBy from "lodash/sortBy";
import { mapActions, mapGetters, mapState } from "vuex";

import TruncateText from "@/components/TruncateText.vue";
import ConfirmChangeModal from "@/Department/ConfirmChangeModal.vue";
import useVuelidate from "@vuelidate/core";
import Modal from "../components/Modal.vue";
import { EventBus } from "../lib/event_bus.js";

export default {
  components: {
    ConfirmChangeModal,
    Modal,
    TruncateText
  },
  props: {
    departmentId: {
      type: Number,
      default: null
    },
    departmentName: {
      type: String,
      default: null
    },
    jobId: {
      type: Number,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  data() {
    return {
      updatedDepartmentId: this.departmentId,
      confirmModalVisible: false,
      departmentChangePreview: null,
      updateAccountingClasses: false
    };
  },
  computed: {
    ...mapGetters(["showAccountingClasses"]),
    ...mapState({
      departments: "departments",
      departmentOptions: ({ departments }) => {
        const sortedDepartments = sortBy(
          departments,
          ({ name, archived }) =>
            archived === false && (name || "").toLowerCase(),
          ({ archived }) => archived === true
        );

        return sortedDepartments.map((dept) => ({
          value: dept.id,
          label: dept.name,
          archived: dept.archived
        }));
      }
    }),
    confirmChanges: ({ showAccountingClasses }) => {
      return showAccountingClasses;
    },
    departmentChanged: ({ departmentId, updatedDepartmentId }) => {
      return departmentId !== updatedDepartmentId;
    },
    submitButtonText: ({ confirmChanges, departmentChanged }) => {
      return confirmChanges && departmentChanged ? "Preview" : "Save";
    }
  },
  mounted() {
    this.fetchDepartments();
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  methods: {
    ...mapActions([
      "fetchDepartments",
      "updateJobDepartment",
      "previewDepartmentChange"
    ]),
    handleSave() {
      this.v$.$validate().then(async (valid) => {
        if (valid) {
          if (this.departmentChanged && this.confirmChanges) {
            await this.promptToConfirmChanges();
          } else {
            await this.commitChanges();
          }
        }
      });
    },
    async commitChanges() {
      const res = await this.updateJobDepartment({
        jobId: this.jobId,
        department_id: this.updatedDepartmentId,
        update_accounting_classes: this.updateAccountingClasses
      });

      window.notify.success("Job department updated successfully!");
      if (res.data.message) {
        window.notify.success(`${res.data.message}`);
      }

      this.$emit("hide-modal");
      this.$emit("save-success");
      EventBus.$emit("refresh-datatable");
    },
    async promptToConfirmChanges() {
      const res = await this.previewDepartmentChange({
        jobId: this.jobId,
        departmentId: this.updatedDepartmentId
      });

      this.departmentChangePreview = res.data;
      this.confirmModalVisible = true;
    },
    async hideConfirmModal() {
      this.confirmModalVisible = false;
      this.departmentChangePreview = null;
    },
    async handleChangesConfirmed(updateAccountingClasses) {
      this.updateAccountingClasses = updateAccountingClasses;

      await this.hideConfirmModal();

      await this.commitChanges();
    }
  }
};
</script>

<style lang="scss">
.job-opportunity-modal {
  .modal-dialog {
    max-width: 500px;
  }
}
</style>
